import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, ChevronRight, Clock,
  ShieldAlert, SlidersHorizontal, Sparkles,
} from 'lucide-react';
import { reportsApi } from '@/api';
import { getPublicOrigin } from '@/utils/booking';
import {
  Report, ReportSection, Severity,
} from '@/types';
import { formatDate, formatCurrency, sortByOrder } from '@/utils/helpers';
import {
  Avatar, Badge, Button, Card, CardHeader, CardTitle, Chip, EmptyState,
  MetricCard, SeverityDot, useToast,
} from '@/ui';
import { ReportHero } from './ClientPortal/ReportHero';
import { SectionCard } from './ClientPortal/SectionCard';
import { FixListEntryCard } from './ClientPortal/FixListEntryCard';
import { BrandFooter, PortalNotFound, PortalSkeleton } from './ClientPortal/states';
import { hexToRgbTriplet, type SeverityFilter } from './ClientPortal/_shared';

interface ClientPortalProps {
  /** Rendered at /r/:slug/summary — condensed variant. */
  summaryMode?: boolean;
}

const ClientPortal: React.FC<ClientPortalProps> = ({ summaryMode = false }) => {
  const { publicId } = useParams<{ publicId: string }>();
  const toast = useToast();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedPhotos, setExpandedPhotos] = useState<Record<string, boolean>>({});
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<SeverityFilter>('all');

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (publicId) {
      loadReport();
      reportsApi.trackPublicView(publicId).catch(() => { /* fire-and-forget */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]);

  const loadReport = async () => {
    try {
      const response = await reportsApi.getPublicReport(publicId!);
      if (response.success && response.data) {
        setReport(response.data);
      } else {
        setError('Report not found or is no longer available.');
      }
    } catch {
      setError('Failed to load report. Please check the link and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!publicId) return;

    // Safari popup blocker workaround: open the tab synchronously in the click handler.
    const pdfTab = window.open('', '_blank');
    if (pdfTab) {
      pdfTab.document.write(`
        <html><head><title>Generating PDF…</title></head>
        <body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;color:#44444a;background:#fafaf9;">
          <div style="text-align:center;">
            <div style="border:3px solid #ebebe9;border-top-color:#2F6A44;border-radius:50%;width:36px;height:36px;animation:spin 0.8s linear infinite;margin:0 auto 18px;"></div>
            <p style="font-size:14px;">Preparing your inspection report…</p>
            <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
          </div>
        </body></html>
      `);
    }

    try {
      setGeneratingPdf(true);
      const response = await reportsApi.generatePublicPDF(publicId);
      if (response.success && response.data?.pdfBase64) {
        const bytes = Uint8Array.from(atob(response.data.pdfBase64), c => c.charCodeAt(0));
        const blobUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        if (pdfTab) pdfTab.location.href = blobUrl;
      } else if (pdfTab) {
        pdfTab.document.body.innerHTML = '<p style="text-align:center;margin-top:40vh;font-family:system-ui;color:#be2631;">Failed to generate PDF. Please close this tab and try again.</p>';
      }
    } catch {
      if (pdfTab) {
        pdfTab.document.body.innerHTML = '<p style="text-align:center;margin-top:40vh;font-family:system-ui;color:#be2631;">Failed to generate PDF. Please close this tab and try again.</p>';
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleShareLink = async () => {
    const url = `${getPublicOrigin()}/r/${publicId}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Inspection report', url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.show({ tone: 'success', title: 'Link copied', description: 'Share it with your agent or co-buyer.' });
      }
    } catch {
      /* user cancelled share sheet — no-op */
    }
  };

  const togglePhotoExpand = useCallback((photoId: string) => {
    setExpandedPhotos(prev => ({ ...prev, [photoId]: !prev[photoId] }));
  }, []);

  // ── Derived data ────────────────────────────────────────────────────────
  const sortedSections: ReportSection[] = useMemo(
    () => report?.sections ? sortByOrder(report.sections) : [],
    [report],
  );

  // A section is visible if it has any findings, any scope-exclusion
  // limitations, any component carrying included notes, or any populated
  // metadata attributes. Metadata-only sections (e.g. Inspection Details)
  // stay visible whenever a single attribute pair is filled in — without
  // that branch they'd disappear from the report even though they carry
  // the on-site context (weather, attendance, occupancy).
  const visibleSections = useMemo(
    () => sortedSections.filter(s =>
      (s.items?.length ?? 0) > 0 ||
      (s.limitations?.length ?? 0) > 0 ||
      (s.components ?? []).some(c => (c.notes?.length ?? 0) > 0) ||
      (s.attributes ?? []).some(g => (g.pairs?.length ?? 0) > 0)
    ),
    [sortedSections],
  );

  const severityCounts = useMemo(() => {
    let safety = 0, recommendation = 0, observation = 0;
    sortedSections.forEach(s => (s.items || []).forEach(i => {
      if (i.severity === 'safety_concern') safety++;
      else if (i.severity === 'recommendation') recommendation++;
      else observation++;
    }));
    return { safety, recommendation, observation, total: safety + recommendation + observation };
  }, [sortedSections]);

  const indexedFindings = useMemo(() => {
    const out: Array<{
      key: string; sectionId: string; sectionTitle: string;
      indexLabel: string; title: string; severity: Severity;
    }> = [];
    visibleSections.forEach((section, sectionIdx) => {
      const items = sortByOrder(section.items || []);
      items.forEach((item, itemIdx) => {
        out.push({
          key: item.id,
          sectionId: section.id,
          sectionTitle: section.title,
          indexLabel: `${sectionIdx + 1}.${itemIdx + 1}`,
          title: item.title,
          severity: item.severity,
        });
      });
    });
    return out;
  }, [visibleSections]);

  const totalCost = useMemo(
    () => sortedSections.reduce(
      (total, s) => total + (s.items || []).reduce((t, i) => t + (i.estimatedCost || 0), 0),
      0,
    ),
    [sortedSections],
  );

  // Apply severity filter.
  const matchesFilter = useCallback((severity: Severity) => {
    if (filterSeverity === 'all') return true;
    return severity === filterSeverity;
  }, [filterSeverity]);

  // Highlight the section currently in view for sticky nav.
  useEffect(() => {
    if (visibleSections.length === 0 || summaryMode) return;
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.getAttribute('data-section-id');
          if (id) setActiveSectionId(id);
        }
      },
      { rootMargin: '-160px 0px -60% 0px', threshold: 0 },
    );
    visibleSections.forEach(s => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [visibleSections, summaryMode]);

  const scrollToSection = (sectionId: string) => {
    const el = sectionRefs.current[sectionId];
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 140;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  // ── Loading / error states ──────────────────────────────────────────────
  if (loading) return <PortalSkeleton />;
  if (error || !report) return <PortalNotFound message={error} />;

  const brand = report.inspector ?? null;
  const brandRgb = hexToRgbTriplet(brand?.brandColor);
  const inspectorName = [brand?.firstName, brand?.lastName].filter(Boolean).join(' ') || 'Your Inspector';
  const companyName = brand?.companyName || 'Flowspect';

  const inspectionDate = report.inspectionDate ? formatDate(report.inspectionDate) : '';
  const reportUrl = `${getPublicOrigin()}/r/${publicId}`;

  const filteredSections = visibleSections.map(section => ({
    ...section,
    items: sortByOrder(section.items || []).filter(i => matchesFilter(i.severity)),
  }));
  // When filtering by severity, keep only sections with at least one matching
  // finding — notes/limitations alone aren't relevant to the severity drill-in.
  // On "all", show every visible section (including note-only / limitation-only).
  const sectionsWithMatches = filteredSections.filter(s =>
    s.items.length > 0 ||
    (filterSeverity === 'all' && (
      (s.limitations?.length ?? 0) > 0 ||
      (s.components ?? []).some(c => (c.notes?.length ?? 0) > 0)
    ))
  );
  const filteredIndexed = indexedFindings.filter(f => matchesFilter(f.severity));

  return (
    <div
      className="min-h-screen bg-surface-0 pb-24 brand-root"
      style={{ '--brand': brandRgb } as React.CSSProperties}
    >
      <ReportHero
        companyName={companyName}
        inspectorName={inspectorName}
        inspectorBrand={brand}
        propertyAddress={report.propertyAddress}
        clientName={report.clientName}
        inspectionDate={inspectionDate}
        heroPhotoUrl={report.heroPhotoUrl ?? null}
        onDownloadPdf={handleDownloadPdf}
        onShare={handleShareLink}
        generatingPdf={generatingPdf}
        reportUrl={reportUrl}
      />

      <main className="max-w-[860px] mx-auto px-4 sm:px-6">
        {summaryMode && (
          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <Badge tone="brand" dot>Summary mode</Badge>
            <Link
              to={`/r/${publicId}`}
              className="text-body-sm font-medium text-brand hover:underline underline-offset-4 inline-flex items-center gap-1"
            >
              View full report <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {!summaryMode && indexedFindings.length > 0 && (
          <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
            <Link
              to={`/r/${publicId}/summary`}
              className="text-body-sm font-medium text-brand hover:underline underline-offset-4 inline-flex items-center gap-1"
            >
              View summary <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <div className="inline-flex items-center gap-2 text-caption text-ink-subtle">
              <Clock className="h-3.5 w-3.5" />
              <span>~{Math.max(2, Math.round(indexedFindings.length * 0.5))} min read</span>
            </div>
          </div>
        )}

        {/* Summary metrics — two cells, Safety + Recommendation. */}
        <section className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <MetricCard
            tone="safety"
            label="Safety concerns"
            value={severityCounts.safety}
            hint="Address before closing"
            icon={<ShieldAlert className="h-4 w-4" />}
          />
          <MetricCard
            tone="recommendation"
            label="Recommendations"
            value={severityCounts.recommendation}
            hint="Consider addressing"
            icon={<Sparkles className="h-4 w-4" />}
          />
        </section>

        {totalCost > 0 && (
          <Card className="mt-4 flex items-center justify-between gap-6">
            <div>
              <p className="text-overline uppercase text-ink-subtle">Estimated repair total</p>
              <p className="mt-1 font-display text-display-sm text-ink nums-tabular" style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 50" }}>
                {formatCurrency(totalCost)}
              </p>
            </div>
            <p className="text-caption text-ink-faint max-w-[220px] text-right">
              Indicative estimates — actual quotes vary by contractor and scope.
            </p>
          </Card>
        )}

        {/* AI Summary — only shown when inspector has generated one */}
        {report?.aiSummary && (
          <Card className="mt-6">
            <p className="text-overline uppercase text-ink-subtle mb-3">Summary</p>
            <div className="prose prose-sm prose-stone max-w-none text-ink-muted">
              <ReactMarkdown>{report.aiSummary}</ReactMarkdown>
            </div>
          </Card>
        )}

        {/* Severity filter — 3 chips: All / Safety / Recommendations */}
        {!summaryMode && indexedFindings.length > 0 && (
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-caption text-ink-subtle mr-1">
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filter
            </span>
            <Chip
              tone="neutral"
              pressed={filterSeverity === 'all'}
              onClick={() => setFilterSeverity('all')}
              count={severityCounts.total}
            >
              All findings
            </Chip>
            <Chip
              tone="sev-critical"
              dot
              pressed={filterSeverity === 'safety_concern'}
              onClick={() => setFilterSeverity('safety_concern')}
              count={severityCounts.safety}
            >
              Safety
            </Chip>
            <Chip
              tone="sev-minor"
              dot
              pressed={filterSeverity === 'recommendation'}
              onClick={() => setFilterSeverity('recommendation')}
              count={severityCounts.recommendation}
            >
              Recommendations
            </Chip>
          </div>
        )}

        {/* Inspector note — "a human wrote this" moment before the scroll */}
        {brand?.signatureText && !summaryMode && (
          <Card variant="elevated" density="roomy" className="mt-6">
            <div className="flex items-start gap-4">
              <Avatar size="lg" name={inspectorName} />
              <div className="min-w-0 flex-1">
                <p className="text-overline uppercase text-ink-subtle">A note from your inspector</p>
                <p className="mt-2 text-body-md text-ink-muted whitespace-pre-line">
                  {brand.signatureText}
                </p>
                <p className="mt-3 text-body-sm text-ink">
                  — {inspectorName}
                  {brand.licenseNumber && <span className="text-ink-subtle"> · Lic. {brand.licenseNumber}</span>}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Fix List entry — gateway into the buyer/agent fix-list flow. */}
        {!summaryMode && publicId && filteredIndexed.length > 0 && (
          <FixListEntryCard publicId={publicId} />
        )}

        {/* Indexed findings list */}
        {filteredIndexed.length > 0 && (
          <Card unpadded className="mt-6 overflow-hidden">
            <CardHeader>
              <div>
                <CardTitle>Findings at a glance</CardTitle>
                <p className="text-caption text-ink-subtle mt-0.5">
                  {filteredIndexed.length} of {indexedFindings.length} finding{indexedFindings.length === 1 ? '' : 's'}
                </p>
              </div>
              {summaryMode && (
                <Link
                  to={`/r/${publicId}`}
                  className="text-body-sm text-brand font-medium inline-flex items-center gap-1 hover:underline underline-offset-4"
                >
                  Open full report <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </CardHeader>
            <ul className="divide-y divide-border-subtle">
              {filteredIndexed.map(f => (
                <li key={f.key}>
                  <button
                    onClick={() => scrollToSection(f.sectionId)}
                    className="w-full text-left px-5 md:px-6 py-3 flex items-center gap-3 hover:bg-surface-2 transition-colors duration-180 group"
                  >
                    <SeverityDot severity={f.severity} />
                    <span className="text-caption font-mono text-ink-faint tabular-nums w-10 shrink-0">
                      {f.indexLabel}
                    </span>
                    <span className="text-body-sm text-ink-subtle truncate w-[32%] sm:w-[30%] shrink-0">
                      {f.sectionTitle}
                    </span>
                    <span className="text-body-sm text-ink font-medium truncate flex-1">
                      {f.title}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity duration-180" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {indexedFindings.length === 0 && (
          <Card className="mt-6">
            <EmptyState
              icon={<CheckCircle2 />}
              title="No findings to review"
              description="Your inspector hasn't recorded any findings for this report yet."
            />
          </Card>
        )}
      </main>

      {/* Sticky section nav */}
      {!summaryMode && visibleSections.length > 1 && (
        <nav
          className="sticky top-0 z-30 glass-nav border-b border-border-subtle mt-10"
          aria-label="Report sections"
        >
          <div className="max-w-[860px] mx-auto px-4 sm:px-6">
            <div className="flex gap-1.5 overflow-x-auto py-3 scrollbar-none">
              {visibleSections.map(section => {
                const isActive = activeSectionId === section.id;
                let safetyN = 0, recommendN = 0;
                (section.items || []).forEach(i => {
                  if (i.severity === 'safety_concern') safetyN++;
                  else if (i.severity === 'recommendation') recommendN++;
                });
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={
                      'shrink-0 inline-flex items-center gap-1.5 px-3 h-8 text-body-sm font-medium rounded-full transition-all duration-180 ' +
                      (isActive
                        ? 'bg-brand text-brand-ink'
                        : 'text-ink-subtle hover:text-ink hover:bg-surface-2')
                    }
                  >
                    <span>{section.title}</span>
                    {safetyN > 0 && (
                      <span
                        className={
                          'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold tabular-nums ' +
                          (isActive ? 'bg-brand-ink/20 text-brand-ink' : 'bg-sev-critical-soft text-sev-critical')
                        }
                        title={`${safetyN} safety concern${safetyN === 1 ? '' : 's'}`}
                      >
                        {safetyN}
                      </span>
                    )}
                    {recommendN > 0 && (
                      <span
                        className={
                          'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold tabular-nums ' +
                          (isActive ? 'bg-brand-ink/20 text-brand-ink' : 'bg-sev-minor-soft text-sev-minor')
                        }
                        title={`${recommendN} recommendation${recommendN === 1 ? '' : 's'}`}
                      >
                        {recommendN}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Sections */}
      {!summaryMode && (
        <div className="max-w-[860px] mx-auto px-4 sm:px-6 mt-8">
          {sectionsWithMatches.length > 0 ? (
            <div className="space-y-6">
              {sectionsWithMatches.map(section => (
                <SectionCard
                  key={section.id}
                  section={section}
                  onTogglePhoto={togglePhotoExpand}
                  expandedPhotos={expandedPhotos}
                  assignRef={el => { sectionRefs.current[section.id] = el; }}
                />
              ))}
            </div>
          ) : (
            <Card>
              <EmptyState
                icon={<CheckCircle2 />}
                title="No findings match this filter"
                description="Try switching back to “All findings” to see the full report."
                action={
                  <Button variant="secondary" size="sm" onClick={() => setFilterSeverity('all')}>
                    Show all findings
                  </Button>
                }
                compact
              />
            </Card>
          )}
        </div>
      )}

      <BrandFooter />
    </div>
  );
};

export default ClientPortal;
